import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { httpsCallable } from "firebase/functions";
import { functions } from "@/lib/firebase";
import { Loader2 } from "lucide-react";

// ─── Cloud Function callables ─────────────────────────────────────────────────

const getIntegrationStatusFn = httpsCallable<
  { endUserId: string },
  {
    success: boolean;
    data?: {
      google: ProviderStatus | null;
      microsoft: ProviderStatus | null;
      primaryProvider: "google" | "microsoft" | null;
      orgLogoUrl: string | null;
      orgIntegrations: {
        google?: { enabled?: boolean };
        microsoft?: { enabled?: boolean };
      } | null;
    };
    error?: string;
  }
>(functions, "getIntegrationStatus");

const initiateGoogleOAuthFn = httpsCallable<
  { endUserId: string },
  { success: boolean; data?: { authUrl: string }; error?: string }
>(functions, "initiateGoogleOAuth");

const initiateMicrosoftOAuthFn = httpsCallable<
  { endUserId: string },
  { success: boolean; data?: { authUrl: string }; error?: string }
>(functions, "initiateMicrosoftOAuth");

const disconnectIntegrationFn = httpsCallable<
  { endUserId: string; provider: "google" | "microsoft" },
  { success: boolean; error?: string }
>(functions, "disconnectIntegration");

// ─── Types ────────────────────────────────────────────────────────────────────

interface ProviderStatus {
  connected: boolean;
  email: string;
  scopes: string[];
  connectedAt: unknown;
  lastUsedAt: unknown;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

// Phase-2 Task 9: the calendar write scope each provider's OAuth consent needs
// for workshop calendar-invite sync (functions/src/workshops/calendarSync.ts).
// Must match initiateOAuth.ts's GOOGLE_SCOPES / MICROSOFT_SCOPES additions.
// Accounts connected BEFORE this change only hold the old .readonly scope —
// getIntegrationStatus already returns each provider's granted `scopes` array,
// so a stale connection is detected here without any new backend call.
const CALENDAR_WRITE_SCOPE: Record<"google" | "microsoft", string> = {
  google: "https://www.googleapis.com/auth/calendar.events",
  microsoft: "Calendars.ReadWrite",
};

function needsCalendarReconnect(
  provider: "google" | "microsoft",
  status: ProviderStatus | null
): boolean {
  if (!status?.connected) return false;
  return !(status.scopes ?? []).includes(CALENDAR_WRITE_SCOPE[provider]);
}

function formatRelativeTime(timestamp: unknown): string | null {
  if (!timestamp) return null;
  // Firestore timestamps come as { _seconds, _nanoseconds } over the wire
  const ts = timestamp as { _seconds?: number; seconds?: number };
  const seconds = ts._seconds ?? ts.seconds;
  if (!seconds) return null;
  const date = new Date(seconds * 1000);
  const diff = Date.now() - date.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

// ─── Google SVG ───────────────────────────────────────────────────────────────

function GoogleLogo() {
  return (
    <svg className="h-5 w-5" viewBox="0 0 24 24">
      <path
        fill="#4285F4"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
      />
      <path
        fill="#34A853"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      />
      <path
        fill="#FBBC05"
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      />
      <path
        fill="#EA4335"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      />
    </svg>
  );
}

function MicrosoftLogo() {
  return (
    <svg className="h-5 w-5" viewBox="0 0 23 23">
      <rect fill="#F25022" x="1" y="1" width="10" height="10" />
      <rect fill="#7FBA00" x="12" y="1" width="10" height="10" />
      <rect fill="#00A4EF" x="1" y="12" width="10" height="10" />
      <rect fill="#FFB900" x="12" y="12" width="10" height="10" />
    </svg>
  );
}

// ─── Provider Card ────────────────────────────────────────────────────────────

function ProviderCard({
  provider,
  label,
  description,
  logo,
  status,
  enabled,
  connecting,
  disconnecting,
  onConnect,
  onDisconnect,
}: {
  provider: "google" | "microsoft";
  label: string;
  description: string;
  logo: React.ReactNode;
  status: ProviderStatus | null;
  enabled: boolean;
  connecting: boolean;
  disconnecting: boolean;
  onConnect: () => void;
  onDisconnect: () => void;
}) {
  const connected = status?.connected === true;
  const lastUsed = connected ? formatRelativeTime(status?.lastUsedAt) : null;

  if (!enabled) {
    return (
      <div className="rounded-xl border border-gray-200 bg-gray-50 p-5 opacity-60">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gray-100">
            {logo}
          </div>
          <div>
            <p className="text-sm font-medium text-gray-400">{label}</p>
            <p className="text-xs text-gray-400">Not available for your organization</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${provider === "google" ? "bg-red-50" : "bg-blue-50"}`}>
            {logo}
          </div>
          <div>
            <p className="text-sm font-medium text-gray-900">{label}</p>
            <p className="text-xs text-gray-500">{description}</p>
          </div>
        </div>

        {connected ? (
          <span className="inline-flex items-center rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-medium text-emerald-700">
            Connected
          </span>
        ) : (
          <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-500">
            Not connected
          </span>
        )}
      </div>

      {connected && status && (
        <div className="mt-3 rounded-lg bg-gray-50 px-3 py-2">
          <p className="text-xs text-gray-600">
            <span className="font-medium">Account:</span> {status.email}
          </p>
          {lastUsed && (
            <p className="mt-0.5 text-xs text-gray-400">
              Last used {lastUsed}
            </p>
          )}
        </div>
      )}

      {connected && needsCalendarReconnect(provider, status) && (
        <button
          onClick={onConnect}
          disabled={connecting}
          className="mt-3 w-full rounded-lg border border-amber-200 bg-amber-50 px-4 py-2 text-sm font-medium text-amber-700 transition-colors hover:bg-amber-100 disabled:opacity-50"
        >
          {connecting ? "Reconnecting..." : "Reconnect to enable calendar invites"}
        </button>
      )}

      <div className="mt-4">
        {connected ? (
          <button
            onClick={onDisconnect}
            disabled={disconnecting}
            className="w-full rounded-lg border border-red-200 bg-white px-4 py-2 text-sm font-medium text-red-600 transition-colors hover:bg-red-50 disabled:opacity-50"
          >
            {disconnecting ? "Disconnecting..." : "Disconnect"}
          </button>
        ) : (
          <button
            onClick={onConnect}
            disabled={connecting}
            className="w-full rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-gray-800 disabled:opacity-50"
          >
            {connecting ? (
              <span className="inline-flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                Connecting...
              </span>
            ) : (
              `Connect ${label}`
            )}
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Page Component ───────────────────────────────────────────────────────────

export function IntegrationsPage() {
  const [searchParams] = useSearchParams();
  const userId = searchParams.get("userId");
  const connectedParam = searchParams.get("connected");

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successBanner, setSuccessBanner] = useState<string | null>(null);
  const [connectingGoogle, setConnectingGoogle] = useState(false);
  const [connectingMicrosoft, setConnectingMicrosoft] = useState(false);
  const [disconnectingGoogle, setDisconnectingGoogle] = useState(false);
  const [disconnectingMicrosoft, setDisconnectingMicrosoft] = useState(false);

  const [googleStatus, setGoogleStatus] = useState<ProviderStatus | null>(null);
  const [microsoftStatus, setMicrosoftStatus] = useState<ProviderStatus | null>(null);
  const [orgLogoUrl, setOrgLogoUrl] = useState<string | null>(null);
  const [googleEnabled, setGoogleEnabled] = useState(false);
  const [microsoftEnabled, setMicrosoftEnabled] = useState(false);

  // Show success banner on redirect back from OAuth
  useEffect(() => {
    if (connectedParam === "google") {
      setSuccessBanner("Google account connected!");
    } else if (connectedParam === "microsoft") {
      setSuccessBanner("Microsoft account connected!");
    }
  }, [connectedParam]);

  // Auto-dismiss success banner
  useEffect(() => {
    if (!successBanner) return;
    const t = setTimeout(() => setSuccessBanner(null), 4000);
    return () => clearTimeout(t);
  }, [successBanner]);

  const fetchStatus = useCallback(async () => {
    if (!userId) return;
    try {
      setLoading(true);
      const result = await getIntegrationStatusFn({ endUserId: userId });
      if (result.data.success && result.data.data) {
        const d = result.data.data;
        setGoogleStatus(d.google);
        setMicrosoftStatus(d.microsoft);
        setOrgLogoUrl(d.orgLogoUrl);
        setGoogleEnabled(d.orgIntegrations?.google?.enabled ?? false);
        setMicrosoftEnabled(d.orgIntegrations?.microsoft?.enabled ?? false);
      } else {
        setError(result.data.error ?? "Failed to load integration status.");
      }
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to load integration status."
      );
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  async function handleConnect(provider: "google" | "microsoft") {
    if (!userId) return;
    const setConnecting =
      provider === "google" ? setConnectingGoogle : setConnectingMicrosoft;
    setConnecting(true);
    setError(null);

    try {
      const fn =
        provider === "google" ? initiateGoogleOAuthFn : initiateMicrosoftOAuthFn;
      const result = await fn({ endUserId: userId });
      if (result.data.success && result.data.data?.authUrl) {
        window.location.href = result.data.data.authUrl;
      } else {
        setError(result.data.error ?? "Failed to start connection.");
        setConnecting(false);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to start connection.");
      setConnecting(false);
    }
  }

  async function handleDisconnect(provider: "google" | "microsoft") {
    if (!userId) return;
    const setDisconnecting =
      provider === "google" ? setDisconnectingGoogle : setDisconnectingMicrosoft;
    setDisconnecting(true);
    setError(null);

    try {
      const result = await disconnectIntegrationFn({
        endUserId: userId,
        provider,
      });
      if (result.data.success) {
        setSuccessBanner(
          `${provider === "google" ? "Google" : "Microsoft"} account disconnected.`
        );
        await fetchStatus();
      } else {
        setError(result.data.error ?? "Failed to disconnect.");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to disconnect.");
    } finally {
      setDisconnecting(false);
    }
  }

  // No userId — friendly error
  if (!userId) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
        <div className="w-full max-w-md text-center">
          <h1 className="text-lg font-semibold text-gray-900">
            Missing Link
          </h1>
          <p className="mt-2 text-sm text-gray-500">
            This page requires a valid user link. Please access it from your AI
            assistant or contact your administrator.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-start justify-center bg-gray-50 px-4 py-12">
      <div className="w-full max-w-[480px]">
        {/* Logo */}
        {orgLogoUrl && (
          <div className="mb-6 flex justify-center">
            <img
              src={orgLogoUrl}
              alt=""
              className="max-h-12 object-contain"
            />
          </div>
        )}

        {/* Header */}
        <div className="mb-6 text-center">
          <h1 className="text-xl font-semibold text-gray-900">
            AI Assistant Integrations
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Connect your accounts so your AI assistant can access your calendar,
            email, and files.
          </p>
        </div>

        {/* Success banner */}
        {successBanner && (
          <div className="mb-4 rounded-lg bg-emerald-50 px-4 py-3 text-center text-sm font-medium text-emerald-700">
            {successBanner}
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="mb-4 rounded-lg bg-red-50 px-4 py-3 text-center text-sm text-red-700">
            {error}
          </div>
        )}

        {/* Loading */}
        {loading ? (
          <div className="space-y-4">
            <div className="h-[140px] animate-pulse rounded-xl border border-gray-200 bg-gray-100" />
            <div className="h-[140px] animate-pulse rounded-xl border border-gray-200 bg-gray-100" />
          </div>
        ) : (
          <div className="space-y-4">
            <ProviderCard
              provider="google"
              label="Google Workspace"
              description="Gmail, Calendar, Drive"
              logo={<GoogleLogo />}
              status={googleStatus}
              enabled={googleEnabled}
              connecting={connectingGoogle}
              disconnecting={disconnectingGoogle}
              onConnect={() => handleConnect("google")}
              onDisconnect={() => handleDisconnect("google")}
            />

            <ProviderCard
              provider="microsoft"
              label="Microsoft 365"
              description="Outlook, Calendar, OneDrive"
              logo={<MicrosoftLogo />}
              status={microsoftStatus}
              enabled={microsoftEnabled}
              connecting={connectingMicrosoft}
              disconnecting={disconnectingMicrosoft}
              onConnect={() => handleConnect("microsoft")}
              onDisconnect={() => handleDisconnect("microsoft")}
            />
          </div>
        )}

        <p className="mt-6 text-center text-xs text-gray-400">
          Your data is encrypted and only accessible to your AI assistant.
        </p>
      </div>
    </div>
  );
}
